import { Alert } from "antd";
import type { ColumnType } from "antd/es/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useEtfHoldings } from "../../../api/services/etf.hooks";
import { ETF_HOLDINGS_PAGE_SIZE, type EtfHolding } from "../../../api/services/etf.type";
import { Link } from "../../../router/LocaleLink";
import { PathGenerators } from "../../../router/paths";
import Table from "../../components/CustomAntdComponents/Table";
import { CustomPagination } from "../../components/CustomPagination/CustomPagination";
import { NumericCell } from "../StockScreener/NumericCell";
import screenerStyles from "../StockScreener/stockScreener.module.css";
import { formatCount, formatEtfPercent, formatNaiveDate } from "../EtfScreener/etfFormat";
import { HoldingStatusBadge } from "./HoldingStatusBadge";
import styles from "./etfDetail.module.css";

/** Constituents we have a stock page for: US tickers, incl. share classes (BRK.B). */
const STOCK_PAGE_TICKER = /^[A-Z][A-Z0-9-]*(\.[A-Z])?$/;

const numeric = (col: ColumnType<EtfHolding>): ColumnType<EtfHolding> => ({
  ...col,
  align: "right",
  onCell: () => ({ className: screenerStyles.numericCol }),
  onHeaderCell: () => ({ className: screenerStyles.numericCol }),
});

/**
 * The fund's basket, one server page at a time, in the gateway's order
 * (largest weight first). Nothing is sorted on screen: that would order one
 * page of the basket while looking like it ordered all of it (§B10).
 */
export const EtfHoldings = ({ ticker }: { ticker: string }) => {
  const { t } = useTranslation();
  const p = "pages.etfDetail.holdings.";
  const [page, setPage] = useState(1);
  const holdings = useEtfHoldings(ticker, page);

  const data = holdings.data;
  const rows = data?.holdings ?? [];
  const total = data?.total ?? 0;
  const pageSize = data?.limit ?? ETF_HOLDINGS_PAGE_SIZE;
  const offset = data?.offset ?? (page - 1) * pageSize;
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));

  const columns: ColumnType<EtfHolding>[] = [
    numeric({
      title: t("pages.etfScreener.columns.no"),
      key: "index",
      // Baskets run past 10,000 constituents.
      width: 60,
      render: (_: unknown, __: EtfHolding, index: number) => offset + index + 1,
    }),
    {
      title: t(`${p}holding`),
      key: "holding",
      width: 240,
      ellipsis: true,
      render: (_: unknown, row: EtfHolding) => (
        <div className={styles.holdingCell}>
          {row.holdingTicker && STOCK_PAGE_TICKER.test(row.holdingTicker) ? (
            <Link
              className={styles.holdingTicker}
              to={PathGenerators.SYMBOL(row.holdingTicker)}
              state={{ symbolSource: "etf_page" }}
            >
              {row.holdingTicker}
            </Link>
          ) : (
            <span className={styles.holdingTicker}>{row.holdingTicker ?? "—"}</span>
          )}
          <span className={styles.holdingName} title={row.holdingName ?? undefined}>
            {row.holdingName ?? "—"}
          </span>
        </div>
      ),
    },
    numeric({
      title: t(`${p}weight`),
      key: "weight",
      width: 84,
      // Negative weights (short legs) are shown as served.
      render: (_: unknown, row: EtfHolding) => (
        <NumericCell>{formatEtfPercent(row.weightPerc)}</NumericCell>
      ),
    }),
    {
      title: t(`${p}status`),
      key: "status",
      width: 130,
      render: (_: unknown, row: EtfHolding) => <HoldingStatusBadge status={row.status} />,
    },
    ...(["Djim", "Ftse", "Msci", "Sp"] as const).map(
      (book): ColumnType<EtfHolding> => ({
        title: t(`pages.etfScreener.columns.rulebook${book}`),
        key: `status${book}`,
        width: 110,
        render: (_: unknown, row: EtfHolding) => (
          <HoldingStatusBadge status={row[`status${book}`]} />
        ),
      }),
    ),
  ];
  const scrollX = columns.reduce(
    (sum, col) => sum + (typeof col.width === "number" ? col.width : 96),
    0,
  );

  return (
    <section className={`${styles.card} ${styles.cardWide}`}>
      <div className={styles.holdingsHeader}>
        <h2 className={styles.cardTitle}>{t(`${p}title`)}</h2>
        {data && total > 0 && (
          // A partial read says what it read (handover §B6).
          <span className={styles.holdingsMeta}>
            {t(`${p}range`, {
              from: formatCount(offset + 1),
              to: formatCount(offset + rows.length),
              total: formatCount(total),
            })}
            {data.asOf ? ` · ${t(`${p}asOf`, { date: formatNaiveDate(data.asOf) })}` : ""}
          </span>
        )}
      </div>

      {holdings.error ? (
        <Alert
          type="error"
          showIcon
          message={t(`${p}loadFailed`)}
          action={
            <button type="button" onClick={() => void holdings.refetch()}>
              {t("pages.etfScreener.errors.retry")}
            </button>
          }
        />
      ) : data && total === 0 ? (
        <p className={styles.empty}>{t(`${p}empty`)}</p>
      ) : (
        <>
          <div className={`${screenerStyles.tableWrap} ${screenerStyles.denseTable}`}>
            <Table<EtfHolding>
              rowKey={(row) => `${row.holdingTicker ?? ""}|${row.holdingName ?? ""}|${row.weightPerc ?? ""}`}
              loading={holdings.isFetching}
              columns={columns}
              dataSource={rows}
              pagination={false}
              size="small"
              bordered
              tableLayout="fixed"
              scroll={{ x: scrollX }}
              className={screenerStyles.denseTable}
            />
          </div>
          {totalPages > 1 && (
            <div className={screenerStyles.paginationWrap}>
              <CustomPagination
                current={page}
                pageSize={pageSize}
                total={total}
                onChange={(next) => setPage(Math.max(1, next))}
              />
            </div>
          )}
        </>
      )}
      <p className={styles.note}>{t(`${p}notRatedNote`)}</p>
    </section>
  );
};
